import { useCallback, useEffect, useRef, useState } from 'react';
import * as realTimeApi from '../../real-time-api-config';
import { getMessages } from '../../services/session';
import {
  createBotMessagePayload,
  createSelfMessagePayload,
  summarizeMessage,
} from '../utils/message-utils';
import { upsertLocalSession } from '../utils/session-util';
import { getUserId } from '../utils/user-util';
import { toPreview } from '../utils/text-util';

/**
 * Owns one conversation: its transcript, the AppSync subscription, and sending.
 *
 * Unlike the web-plugin, a session id here is long-lived (query param or
 * cookie), so mounting must *load* the existing transcript rather than assume
 * an empty one - and the "Hi" kickoff only fires for genuinely new sessions.
 */
export function useConversation({ appId, accessKey, sessionId, isNewSession, requestAttributes }) {
  const [messages, setMessages] = useState([]);
  const [botReplyWait, setBotReplyWait] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Read inside callbacks that must not re-subscribe when this changes.
  const requestAttributesRef = useRef(requestAttributes);
  requestAttributesRef.current = requestAttributes;

  const appendMessage = useCallback(message => {
    setMessages(previous => [...previous, message]);
  }, []);

  /**
   * Whether *this* session id was minted in this browser, latched on first
   * sight.
   *
   * The caller flips its own `isNew` flag as soon as the visitor sends
   * something, and that flip lands before the greeting effect has run. Reading
   * the prop directly would therefore lose the decision, so it is captured per
   * session id and never retracted.
   */
  const freshnessRef = useRef({ sessionId: null, isNew: false });
  if (freshnessRef.current.sessionId !== sessionId) {
    freshnessRef.current = { sessionId, isNew: Boolean(isNewSession) };
  }
  const isFreshSession = freshnessRef.current.isNew;

  /* ---------------- transcript ---------------- */

  useEffect(() => {
    let cancelled = false;

    async function loadHistory() {
      if (!appId || !sessionId) {
        return;
      }

      // A session this browser just minted has no server-side history; going to
      // the API for it would only add a round trip before the first paint.
      if (isFreshSession) {
        setMessages([]);
        return;
      }

      setHistoryLoading(true);
      setMessages([]);

      const response = await getMessages(appId, sessionId, accessKey);
      if (cancelled) {
        return;
      }

      const restored = (response.data || []).map(entry =>
        entry.sentBy === 'bot'
          ? createBotMessagePayload(entry.message, entry.createdAt, entry.messageId)
          : createSelfMessagePayload(entry.message, entry.createdAt, entry.messageId)
      );

      setMessages(restored);
      setHistoryLoading(false);
    }

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [appId, sessionId, accessKey, isFreshSession]);

  /* ---------------- realtime ---------------- */

  useEffect(() => {
    if (!appId || !sessionId) {
      return undefined;
    }

    let subscription;
    let disposed = false;

    const subscribe = () => {
      subscription = realTimeApi.subscribeBotMessage(
        accessKey,
        appId,
        sessionId,
        ({ data }) => {
          let payload;
          try {
            payload = JSON.parse(data);
          } catch (e) {
            console.debug('unparsable bot message', e);
            return;
          }

          setBotReplyWait(false);
          appendMessage(createBotMessagePayload(payload.message));

          // Keep the rail's preview in step with the latest reply.
          const preview = toPreview(summarizeMessage(payload.message));
          if (preview) {
            upsertLocalSession(appId, {
              sessionId,
              displayMessage: preview,
              modifiedAt: new Date().toISOString(),
            });
          }
        },
        error => {
          const message = error?.error?.errors?.[0]?.message;
          if (message === 'Connection closed' && !disposed) {
            // AppSync drops idle sockets; re-open so a later reply is not lost.
            subscription?.unsubscribe?.();
            subscribe();
          } else {
            console.debug('subscribeBotMessage error', error);
          }
        }
      );
    };

    subscribe();

    return () => {
      disposed = true;
      subscription?.unsubscribe?.();
    };
  }, [appId, accessKey, sessionId, appendMessage]);

  /* ---------------- sending ---------------- */

  const sendMessage = useCallback(
    async (inputText, attributes = {}, appendToUI = true) => {
      if (!appId || !sessionId) {
        return;
      }

      if (appendToUI) {
        appendMessage(createSelfMessagePayload(attributes.title || inputText));
        upsertLocalSession(appId, {
          sessionId,
          displayMessage: toPreview(attributes.title || inputText),
          modifiedAt: new Date().toISOString(),
        });
      }

      setBotReplyWait(true);

      try {
        await realTimeApi.sendUserMessage(
          accessKey,
          appId,
          sessionId,
          JSON.stringify({
            action: 'executor',
            appId,
            sessionId,
            inputText,
            requestAttributes: {
              // The visitor's stable identity. Resolved from its cookie on
              // every send rather than captured once, so "New chat" and a
              // reset both take effect without remounting the hook. Placed
              // first so an explicit `request_attributes` userId from the host
              // page still wins.
              userId: getUserId(),
              ...attributes,
              ...(requestAttributesRef.current || {}),
            },
          })
        );
      } catch (err) {
        console.debug('sendUserMessage failed', err);
        setBotReplyWait(false);
        appendMessage(
          createBotMessagePayload({
            type: 'TEXT',
            content: 'Sorry, that message could not be delivered. Please try again.',
          })
        );
      }
    },
    [appId, accessKey, sessionId, appendMessage]
  );

  /* ------------- greeting kickoff -------------
   * A new session opens with a silent "Hi" so the agent speaks first - the
   * same convention the web-plugin uses.
   *
   * `greetedSessionRef` holds the session already greeted, which both stops
   * StrictMode's double-effect from sending twice and allows the next session
   * to be greeted in turn.
   *
   * Note there is deliberately no "already has messages" check here. The
   * transcript is cleared by the history effect, which runs *after* this one,
   * so on a second "New chat" the previous session's messages are still in
   * state - testing them skipped every other greeting. Freshness is decided by
   * `isFreshSession` (latched per session id) instead, which is the question
   * actually being asked.
   */
  const greetedSessionRef = useRef(null);

  useEffect(() => {
    if (!appId || !sessionId || !isFreshSession || historyLoading) {
      return;
    }
    if (greetedSessionRef.current === sessionId) {
      return;
    }

    greetedSessionRef.current = sessionId;
    sendMessage('Hi', {}, false);
  }, [appId, sessionId, isFreshSession, historyLoading, sendMessage]);

  return { messages, botReplyWait, historyLoading, sendMessage };
}
